import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, RouterOutlet} from '@angular/router';
import {of, retry, Subscription} from 'rxjs';
import {filter, first, switchMap, tap} from 'rxjs/operators';
import {CartService} from './shopping-cart/cart.service';
import {Cart} from './shopping-cart/Cart';
import {SearchService} from './search/search.service';
import {LicenseType, Messages, Product, ProductService, Status, User, UserService} from '@uofu-uss-aux/software-catalog-ng-core';
import {NotificationService} from '@uofu-uss/angular-util';
import {LoggedInStatus, LoginService} from '@uofu-uss/ngx-auth';
import {SearchComponent} from './search/search/search.component';
import {NgIf} from '@angular/common';
import {HeaderComponent} from './header/header.component';
import {FooterComponent} from './footer/footer.component';
import {
  PageAlertComponent
} from './lib/notification/page-alert/page-alert.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    SearchComponent,
    NgIf,
    HeaderComponent,
    RouterOutlet,
    FooterComponent,
    PageAlertComponent,
  ]
})
export class AppComponent implements OnDestroy, OnInit {

  public ready = false;
  public searching = false;
  public products: ReadonlyArray<Product> = [];
  private subscription = new Subscription();

  constructor(private readonly route: ActivatedRoute,
              private readonly userService: UserService,
              private readonly cartService: CartService,
              private readonly loginService: LoginService,
              private readonly notificationService: NotificationService,
              private readonly searchService: SearchService,
              private readonly productService: ProductService) {
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    const loginObserver = {
      next: this.loginComplete,
      error: this.loginFailure,
    };

    this.subscription.add(
        this.loginService.login()
            .pipe(
                tap((status: LoggedInStatus) => this.loginService.loggedInStatus = status),
                switchMap((status: LoggedInStatus) =>
                    status === LoggedInStatus.LoggedIn ?
                        this.userService.fetchLoggedInUser().pipe(retry(1)) :
                        of(UserService.emptyUser)))
            .subscribe(loginObserver));

    this.subscription.add(
        this.loginService.loggedInStatusObservable.pipe(
            filter(s => s === LoggedInStatus.LoggedOut))
            .subscribe(s => this.notificationService.addWarning('You are not logged in. You should be redirected to sign-in shortly')));

    this.searchService.observable.subscribe((v) => this.searching = v);
  }

  private readonly loginComplete = (user: User) => {
    this.ready = true;
    this.userService.value = user;
    if (this.userService.isUserDefined(user)) {
      const observer = {
        next: this.cartLoadSuccess,
        error: this.cartLoadError
      };
      this.cartService.fetchCurrentShoppingCart(user.unid).subscribe(observer);
      const licenseTypes = user.employee ? null : [LicenseType.Student];
      const productFilter = {licenseTypes, productStatus: Status.Active, licenseStatus: Status.Active, offeringStatus: Status.Active};
      this.productService.fetchProducts(productFilter)
          .pipe(first())
          .subscribe(this.initProducts);
    }
  }

  private readonly loginFailure = (err?: any) => {
    this.ready = true;
    this.notificationService.addWarning(Messages.LOGIN_MSG);
  }

  private readonly cartLoadSuccess = (cart: Cart) => {
    this.cartService.value = cart;
  }

  private readonly cartLoadError = () => {
    this.cartService.value = null;
    this.notificationService.addError(Messages.LOAD_CART_MSG);
  }

  private readonly initProducts = (products: readonly Product[]) => {
    this.products = products
      .map(p => this.productService.filterEmptyLicenses(p))
      .filter(p => p.licenses && p.licenses.length > 0);
  }

  onActivate(): void {
    if (window.scrollTo) {
      window.scrollTo(0, 0);
    }
  }
}
